import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useParams } from 'react-router'
import InboxContext, {
  type InboxContextProps,
} from '@/components/inbox/context'
import inboxApi from '@/state/inbox/inbox.slice'
import { initThread } from '@/state/entities/inbox/inbox.types'
import Drawer from '@/pages/[org_id]/venues/[serial]/bookings/components/drawer'
import ThreadCreateSendMessageBox from './create'

export interface InboxAction {
  label: string
  icon?: React.ComponentType<{ className?: string }>
  onClick: (setValue: (content: string) => void) => void | Promise<void>
  variant?: 'primary' | 'secondary' | 'ghost_default'
  isLoading?: boolean
}

export interface InboxCreateWithType {
  contacts: { email: string; name: string }[]
  serial?: string
  subject: string
  content: string
  actions?: InboxAction[]
}
export interface InboxPassedProps extends InboxContextProps {
  isOpen: boolean
  onClose: () => void
  start_text?: string
  createWith: InboxCreateWithType
}

export function CreateOrChatContext({
  createWith,
  ...rest
}: Omit<InboxPassedProps, 'isOpen' | 'onClose'>) {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()
  const { data: contextThreads } = inboxApi.useGetInboxThreadsQuery(
    {
      orgId,
      query: {
        context_id: rest.context_id,
        context_type: rest.context_type,
        serial: rest.serial,
      },
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  const selectedThread = useMemo(() => {
    if (!contextThreads)
      return {
        ...initThread,
        fake_thread: true,
      }
    return (
      contextThreads.data.find(
        (i: any) => i.context_id === rest.context_id
      ) || {
        ...initThread,
        fake_thread: true,
      }
    )
  }, [contextThreads, rest?.context_id])

  return (
    <>
      {!selectedThread?.id ? (
        <ThreadCreateSendMessageBox
          context={{
            id: rest.context_id,
            type: rest.context_type,
          }}
          createWith={createWith}
          onComplete={() => {}}
        />
      ) : null}
      {rest.context_id ? (
        <InboxContext {...{ ...rest, content: createWith.content }} />
      ) : null}
    </>
  )
}

function InboxContextModal({
  isOpen,
  onClose,
  createWith,
  ...rest
}: InboxPassedProps) {
  return (
    <Drawer
      open={isOpen}
      setOpen={(o: any) => {
        if (!o) {
          onClose()
        }
      }}
      size="md"
      title="Start conversation"
    >
      <CreateOrChatContext createWith={createWith} {...rest} />
    </Drawer>
  )
}

type InboxCreateType = InboxContextProps & InboxCreateWithType

type InboxContextType = [
  InboxCreateType | null,
  (value: InboxCreateType | null) => void,
]

const InboxWrapContext = createContext<Partial<InboxContextType>>([])

export function InboxModalProvider({ children }: PropsWithChildren) {
  const [inboxContext, setInboxContext] = useState<InboxCreateType | null>(null)
  return (
    <InboxWrapContext.Provider value={[inboxContext, setInboxContext]}>
      {inboxContext ? (
        <InboxContextModal
          context_id={inboxContext?.context_id}
          context_type={inboxContext?.context_type}
          createWith={inboxContext}
          isOpen={Boolean(inboxContext)}
          onClose={() => {
            setInboxContext(null)
          }}
          serial={inboxContext?.serial}
        />
      ) : null}
      {children}
    </InboxWrapContext.Provider>
  )
}

export const useInboxContext = () => {
  return useContext(InboxWrapContext)
}

export default InboxContextModal
